import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'bvesuvio_cart';

/** Campi minimi da persistere — NO image_url (base64 può pesare 200-500 KB per prodotto) */
function minimalProduct(p) {
  return {
    id: p.id,
    name: p.name,
    price: p.price || 0,
    category: p.category,
    featured: p.featured,
    available: p.available,
    ean: p.ean || '',
    codart: p.codart || '',
    name_mdd: p.name_mdd || '',
    prezzo_mdd: p.prezzo_mdd || 0,
    prezzo_golosi: p.prezzo_golosi || p.price || 0,
  };
}

/** Legge il carrello dal localStorage e migra immediatamente i vecchi dati (rimuove image_url) */
function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Migra: rimuove image_url da eventuali item salvati con il vecchio codice
    const migrated = parsed.map(({ product, quantity }) => ({
      product: minimalProduct(product || {}),
      quantity: Math.max(1, parseInt(quantity) || 1),
    }));
    // Scrivi subito la versione pulita per liberare spazio
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
    } catch { localStorage.removeItem(STORAGE_KEY); }
    return migrated;
  } catch {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    try {
      const minimal = items.map(({ product, quantity }) => ({ product: minimalProduct(product), quantity }));
      // Rimuovi prima il valore vecchio, poi scrivi — evita che il browser conti entrambi
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(minimal));
    } catch {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }
  }, [items]);

  const addToCart = (product, qty = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) return prev.map(i => i.product.id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { product, quantity: qty }];
    });
  };

  const removeFromCart = (productId) => setItems(prev => prev.filter(i => i.product.id !== productId));

  const updateQty = (productId, qty) => {
    if (qty <= 0) { removeFromCart(productId); return; }
    setItems(prev => prev.map(i => i.product.id === productId ? { ...i, quantity: qty } : i));
  };

  const clearCart = () => setItems([]);

  const [cartOpen, setCartOpen] = useState(false);
  const openCart  = () => setCartOpen(true);
  const closeCart = () => setCartOpen(false);

  const total = items.reduce((s, i) => s + (i.product.price || 0) * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, updateQty, clearCart, total, count, cartOpen, openCart, closeCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
